import { format, isSameDay, addDays, startOfWeek } from 'date-fns';

/**
 * Task Batching Service
 *
 * Provides intelligent dynamic batching of college tasks to reduce overwhelm
 * while maintaining flexibility for individual task management.
 */

// Task types that are good candidates for batching
const BATCHABLE_TASK_TYPES = {
  // Quick administrative tasks
  COLLEGE_DATA: ['populate_college_data', 'verify_deadlines', 'update_college_info'],

  // Research and exploration
  RESEARCH: ['college_research', 'strategic_research', 'campus_culture_research', 'program_research'],

  // Value propositions and quick writing
  VALUE_PROPS: ['value_proposition', 'why_this_college', 'fit_statement'],

  // Application setup
  APPLICATION_SETUP: ['create_portal_account', 'start_application', 'add_to_common_app'],

  // Document requests
  DOCUMENTS: ['request_transcript', 'send_scores', 'submit_documents'],

  // Financial aid
  FINANCIAL: ['css_profile_school', 'fafsa_school_code', 'financial_aid_forms']
};

// College grouping strategies - now primarily by admission tier
const COLLEGE_TIERS = {
  REACH: 'reach',
  TARGET: 'target',
  SAFETY: 'safety'
};

// Legacy grouping for reference (can be used as secondary grouping)
const COLLEGE_GROUPS = {
  IVY_LEAGUE: ['Harvard', 'Yale', 'Princeton', 'Columbia', 'Brown', 'Dartmouth', 'Cornell', 'UPenn'],
  UC_SYSTEM: ['UCLA', 'UC Berkeley', 'UCSD', 'UCSB', 'UCI', 'UC Davis', 'UCSC', 'UCR', 'UC Merced'],
  LIBERAL_ARTS: ['Amherst', 'Williams', 'Swarthmore', 'Pomona', 'Bowdoin', 'Middlebury', 'Wellesley', 'Carleton'],
  TECH_FOCUSED: ['MIT', 'Caltech', 'Carnegie Mellon', 'Georgia Tech', 'RPI', 'WPI', 'Stevens'],
  BIG_TEN: ['Michigan', 'Northwestern', 'Wisconsin', 'Illinois', 'Purdue', 'Ohio State', 'Penn State']
};

class TaskBatchingService {
  constructor() {
    this.batchCounter = 0;
  }

  /**
   * Main entry point for creating dynamic batches from tasks
   * @param {Array} tasks - All tasks for the user
   * @param {Date} currentDate - Current date for context
   * @param {Object} options - Batching options
   * @returns {Array} - Mixed array of batch objects and individual tasks
   */
  createDynamicBatches(tasks, currentDate, options = {}) {
    const {
      maxBatchSize = 6,
      minBatchSize = 3,
      preferredBatchSize = 4,
      respectDeadlines = true,
      groupBySimilarity = true
    } = options;

    // Separate batchable and non-batchable tasks
    const { batchable, nonBatchable } = this.separateTasks(tasks);

    // Group batchable tasks by type and date
    const groupedTasks = this.groupTasksByTypeAndDate(batchable);

    // Create intelligent batches
    const batches = [];

    Object.entries(groupedTasks).forEach(([key, taskGroup]) => {
      const [taskType, roundKey, dateStr] = key.split('|');

      if (taskGroup.length >= minBatchSize) {
        // Create batches based on similarity if enabled
        if (groupBySimilarity) {
          const similarityBatches = this.createSimilarityBatches(
            taskGroup,
            preferredBatchSize,
            maxBatchSize
          );
          batches.push(...similarityBatches.map(batch =>
            this.formatBatch(batch, taskType, roundKey, dateStr)
          ));
        } else {
          // Simple size-based batching
          const sizeBatches = this.createSizeBatches(
            taskGroup,
            preferredBatchSize,
            maxBatchSize
          );
          batches.push(...sizeBatches.map(batch =>
            this.formatBatch(batch, taskType, roundKey, dateStr)
          ));
        }
      } else {
        // Too few tasks to batch, keep as individual
        nonBatchable.push(...taskGroup);
      }
    });

    // Combine batches and individual tasks
    return [...batches, ...nonBatchable];
  }

  /**
   * Separate tasks into batchable and non-batchable
   */
  separateTasks(tasks) {
    const batchable = [];
    const nonBatchable = [];

    tasks.forEach(task => {
      // Check if task is batchable based on metadata and type
      if (this.isTaskBatchable(task)) {
        batchable.push(task);
      } else {
        nonBatchable.push(task);
      }
    });

    return { batchable, nonBatchable };
  }

  /**
   * Determine if a task is batchable
   * Enhanced with availability checking and event-based blacklist
   */
  isTaskBatchable(task) {
    // Don't batch completed tasks
    if (task.isCompleted || task.status === 'completed') return false;

    // Don't batch overdue tasks (handle separately)
    if (task.isOverdue) return false;

    // Check if task has explicit batching metadata
    if (task.metadata?.batchable === false) return false;

    // Check task title for batchable patterns (fallback for tasks without metadata)
    const title = (task.title || '').toLowerCase();

    // NEW: Blacklist event-based tasks that shouldn't batch across colleges
    const NON_BATCHABLE_PATTERNS = [
      /visit.*campus/i,
      /campus.*visit/i,
      /\binterview\b/i,
      /\btest\b.*date/i,
      /take.*test/i,
      /\bdeadline\b/i,
      /submit.*application/i,
      /attend.*event/i,
      /college.*visit/i,
      /meet.*with/i,
      /appointment/i
    ];

    if (NON_BATCHABLE_PATTERNS.some(pattern => pattern.test(title))) {
      return false;
    }

    // Check if task is available (e.g., AP registration has specific windows)
    if (!this.isTaskAvailable(task)) return false;

    // Value proposition tasks are highly batchable
    if (title.includes('value proposition') ||
        title.includes('why this college') ||
        (title.includes('complete value') && title.includes('for'))) {
      return true;
    }

    // Research tasks
    if (task.category === 'research' || title.includes('research')) {
      return true;
    }

    // Profile tasks that are college-specific
    if ((task.category === 'profile' || task.category === 'Profile & Foundation') &&
        task.college_id &&
        title.includes('for')) {
      return true;
    }

    // Application setup
    if (title.includes('portal') || title.includes('application account')) {
      return true;
    }

    // Check if task type is in batchable list (if metadata exists)
    const taskSubtype = task.metadata?.subtype || task.source_id;
    if (taskSubtype) {
      return Object.values(BATCHABLE_TASK_TYPES).some(types =>
        types.includes(taskSubtype)
      );
    }

    return false;
  }

  /**
   * Group tasks by type, application round, and scheduled date
   * Enhanced to only group tasks within temporal proximity and same application round
   */
  groupTasksByTypeAndDate(tasks) {
    const groups = {};
    const MAX_DAY_SPREAD = 14; // Maximum days between tasks to batch them

    tasks.forEach(task => {
      const taskType = this.getTaskBatchType(task);
      const taskRound = this.getApplicationRound(task);
      const taskDate = task.scheduled_date ? new Date(task.scheduled_date) : null;

      // Find existing group within temporal proximity and same round
      let foundGroup = false;

      if (taskDate) {
        Object.keys(groups).forEach(groupKey => {
          if (foundGroup) return;

          const [groupType, groupRound, groupDateStr] = groupKey.split('|');
          if (groupType !== taskType) return;
          if (groupRound !== taskRound) return; // Must match application round
          if (groupDateStr === 'unscheduled') return;

          const groupDate = new Date(groupDateStr);
          const daysDiff = Math.abs((taskDate - groupDate) / (1000 * 60 * 60 * 24));

          // Only group if tasks are within MAX_DAY_SPREAD days
          if (daysDiff <= MAX_DAY_SPREAD) {
            groups[groupKey].push(task);
            foundGroup = true;
          }
        });
      }

      // Create new group if not found
      if (!foundGroup) {
        const dateKey = task.scheduled_date || 'unscheduled';
        const groupKey = `${taskType}|${taskRound}|${dateKey}`;

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(task);
      }
    });

    return groups;
  }

  /**
   * Get the application round for a task
   * Used to group ED/EA/RD tasks separately
   */
  getApplicationRound(task) {
    // Check task's college application_round
    if (task.application_round) {
      return task.application_round;
    }

    // Check if task has college data with round info
    if (task.college_application_round) {
      return task.college_application_round;
    }

    // Check metadata
    if (task.metadata?.application_round) {
      return task.metadata.application_round;
    }

    // Default to regular decision
    return 'regular';
  }

  /**
   * Get the batch type category for a task
   */
  getTaskBatchType(task) {
    const taskSubtype = task.metadata?.subtype || task.source_id || '';
    const title = (task.title || '').toLowerCase();

    // Check metadata/subtype first
    for (const [category, types] of Object.entries(BATCHABLE_TASK_TYPES)) {
      if (types.includes(taskSubtype)) {
        return category;
      }
    }

    // Fallback to title-based categorization
    if (title.includes('value proposition') || title.includes('why this college')) {
      return 'VALUE_PROPS';
    }
    if (task.category === 'research' || title.includes('research')) {
      return 'RESEARCH';
    }
    if (title.includes('portal') || title.includes('application account')) {
      return 'APPLICATION_SETUP';
    }
    if (title.includes('css') || title.includes('fafsa')) {
      return 'FINANCIAL';
    }

    return 'MISC';
  }

  /**
   * Create batches based on college similarity
   */
  createSimilarityBatches(tasks, preferredSize, maxSize) {
    const batches = [];

    // Group by college tier first (reach/reach, target, safety)
    const tasksByTier = {
      reach: [],
      target: [],
      safety: [],
      unclassified: []
    };

    tasks.forEach(task => {
      // Check if task has tier information in metadata or college data
      const tier = this.getCollegeTier(task);
      const tierGroup = tasksByTier[tier] || tasksByTier.unclassified;
      tierGroup.push(task);
    });

    // Create batches for each tier separately
    // This ensures reach schools are grouped together, targets together, etc.
    const tierOrder = ['reach', 'target', 'safety', 'unclassified'];

    tierOrder.forEach(tier => {
      const tierTasks = tasksByTier[tier];
      if (tierTasks.length === 0) return;

      // For each tier, create batches of preferredSize (usually 3)
      for (let i = 0; i < tierTasks.length; i += preferredSize) {
        const batch = tierTasks.slice(i, Math.min(i + maxSize, tierTasks.length));

        // Only create a batch if it has at least minBatchSize tasks (usually 3)
        // Or if it's the last remaining tasks for this tier
        if (batch.length >= 2 || (i === 0 && batch.length > 0)) {
          batches.push(batch);
        } else if (batch.length === 1 && batches.length > 0) {
          // Add single remaining task to the previous batch if possible
          const lastBatch = batches[batches.length - 1];
          if (lastBatch.length < maxSize) {
            lastBatch.push(...batch);
          } else {
            batches.push(batch);
          }
        } else {
          batches.push(batch);
        }
      }
    });

    return batches;
  }

  /**
   * Create simple size-based batches
   */
  createSizeBatches(tasks, preferredSize, maxSize) {
    const batches = [];

    for (let i = 0; i < tasks.length; i += preferredSize) {
      const batch = tasks.slice(i, Math.min(i + maxSize, tasks.length));
      batches.push(batch);
    }

    return batches;
  }

  /**
   * Get the admission tier for a college task
   */
  getCollegeTier(task) {
    // Always use the current category from the database first
    if (task.college_category) {
      // Map all the various category names to our three main tiers
      const categoryMap = {
        // reach/Reach tier
        'reach': 'reach',
        'reach': 'reach',
        'aspirational': 'reach',
        'stretch': 'reach',

        // Target tier
        'target': 'target',
        'match': 'target',
        'realistic': 'target',
        'competitive': 'target',
        'moderate': 'target',

        // Safety tier
        'safety': 'safety',
        'safe': 'safety',
        'likely': 'safety',
        'backup': 'safety',

        // Other
        'other': 'unclassified'
      };

      return categoryMap[task.college_category] || 'unclassified';
    }

    // Check if tier is explicitly set in metadata (legacy)
    if (task.metadata?.tier) {
      return task.metadata.tier;
    }

    // Check if tier is in the task or college object (legacy)
    if (task.college_tier) {
      return task.college_tier;
    }

    // Fallback: Try to infer from college name using known patterns
    const collegeName = (task.college_name || '').toLowerCase();

    // reach/Reach schools (typically < 20% acceptance rate)
    const reachSchools = [
      'harvard', 'yale', 'princeton', 'stanford', 'mit', 'caltech',
      'columbia', 'penn', 'duke', 'chicago', 'northwestern', 'johns hopkins',
      'brown', 'dartmouth', 'cornell', 'vanderbilt', 'rice', 'notre dame',
      'georgetown', 'berkeley', 'ucla' // Top UCs are reaches for most
    ];

    // Safety schools (typically > 60% acceptance rate or state schools for in-state)
    const safetySchools = [
      'state', 'community', 'cal state', 'csu', // State schools
      'arizona state', 'oregon state', 'washington state'
    ];

    // Check if it's a reach/reach school
    if (reachSchools.some(school => collegeName.includes(school))) {
      return 'reach';
    }

    // Check if it's a safety school
    if (safetySchools.some(school => collegeName.includes(school))) {
      return 'safety';
    }

    // Check for UC schools (mid-tier UCs are typically targets)
    if (collegeName.includes('uc ') || collegeName.includes('university of california')) {
      // Top UCs are reaches (already handled above)
      // Mid-tier UCs are targets
      if (collegeName.includes('davis') || collegeName.includes('irvine') ||
          collegeName.includes('santa barbara') || collegeName.includes('san diego')) {
        return 'target';
      }
      // Lower UCs might be safeties
      if (collegeName.includes('riverside') || collegeName.includes('merced') ||
          collegeName.includes('santa cruz')) {
        return 'safety';
      }
    }

    // Default to target for unclassified schools
    return 'target';
  }

  /**
   * Get college group for similarity matching
   */
  getCollegeGroup(collegeName) {
    if (!collegeName) return 'GENERAL';

    const normalizedName = collegeName.toLowerCase();

    // Check predefined groups first
    for (const [group, colleges] of Object.entries(COLLEGE_GROUPS)) {
      if (colleges.some(college =>
        normalizedName.includes(college.toLowerCase()) ||
        collegeName.includes(college)
      )) {
        return group;
      }
    }

    // Special handling for UC system (various formats)
    if (normalizedName.includes('university of california') ||
        normalizedName.includes('uc ') ||
        normalizedName.includes('berkeley') ||
        normalizedName.includes('ucla')) {
      return 'UC_SYSTEM';
    }

    // Ivy League schools
    if (normalizedName.includes('harvard') || normalizedName.includes('yale') ||
        normalizedName.includes('princeton') || normalizedName.includes('columbia') ||
        normalizedName.includes('brown') || normalizedName.includes('dartmouth') ||
        normalizedName.includes('cornell') || normalizedName.includes('penn')) {
      return 'IVY_LEAGUE';
    }

    // Try to infer from name patterns
    if (normalizedName.includes('state')) {
      return 'STATE_SCHOOLS';
    }
    if (normalizedName.includes('institute') || normalizedName.includes('tech')) {
      return 'TECH_FOCUSED';
    }

    return 'OTHER';
  }

  /**
   * Format a batch of tasks into a batch object
   * Enhanced with better time calculation, validation, and application round grouping
   */
  formatBatch(tasks, taskType, roundKey, dateStr) {
    const collegeNames = [...new Set(tasks.map(t => t.college_name).filter(Boolean))];
    const totalHours = tasks.reduce((sum, t) => {
      // Handle various hour formats
      const hours = t.estimated_hours || t.estimated_duration || 0.75;
      return sum + (typeof hours === 'string' ? parseFloat(hours) : hours);
    }, 0);

    // Calculate actual time savings from batching
    const individualTime = totalHours;
    const batchedTime = this.calculateBatchedTime(tasks, taskType);
    const timeSavings = Math.max(0, individualTime - batchedTime);
    const savingsPercent = individualTime > 0 ? Math.round((timeSavings / individualTime) * 100) : 0;

    // Determine batch title based on type, tier, and application round
    const batchTitle = this.getBatchTitle(taskType, roundKey, tasks);

    // Determine if this is a session or a batch
    const isSession = ['RESEARCH', 'VALUE_PROPS'].includes(taskType);

    // Determine tier information
    const tiers = [...new Set(tasks.map(t => this.getCollegeTier(t)))];
    const isSingleTier = tiers.length === 1;
    const tierName = isSingleTier ? tiers[0] : null;

    // CRISIS SUPPORT FIX: Use earliest actual task date, not grouping key
    // This ensures batches respect crisis support deferrals
    const actualDates = tasks
      .map(t => t.scheduled_date || t.due_date)
      .filter(Boolean)
      .map(d => new Date(d));

    const earliestDate = actualDates.length > 0
      ? new Date(Math.min(...actualDates)).toISOString().split('T')[0]
      : (dateStr !== 'unscheduled' ? dateStr : null);

    console.log(`[Batch Date] Type: ${taskType}, Round: ${roundKey}, Grouping date: ${dateStr}, Earliest task date: ${earliestDate}, Task count: ${tasks.length}`);

    return {
      id: `batch-${taskType}-${roundKey}-${dateStr}-${++this.batchCounter}-${Math.random().toString(36).substr(2, 5)}`,
      application_round: roundKey,
      isBatch: true,
      isSession,
      type: 'batch',
      batchType: taskType,
      title: batchTitle,
      subtitle: this.getBatchSubtitle(collegeNames, tasks.length),
      tasks: tasks,
      taskCount: tasks.length,
      completedCount: tasks.filter(t => t.isCompleted).length,
      scheduled_date: earliestDate,
      display_date: earliestDate,
      estimated_hours: totalHours,
      colleges: collegeNames,
      isExpanded: false,
      canCompleteAll: true,
      sessionNumber: null, // Will be set if part of multi-day session
      totalSessions: null,
      category: tasks[0]?.category || 'admin',
      priority: this.calculateBatchPriority(tasks),
      tier: tierName,
      mixedTiers: !isSingleTier,
      timeSavings: savingsPercent > 20 ? `Save ${savingsPercent}% time` : null,
      batchedTime: batchedTime
    };
  }

  /**
   * Calculate actual time for batched tasks
   * Accounts for context switching and setup time savings
   */
  calculateBatchedTime(tasks, taskType) {
    const baseTime = tasks.reduce((sum, t) => {
      const hours = t.estimated_hours || t.estimated_duration || 0.75;
      return sum + (typeof hours === 'string' ? parseFloat(hours) : hours);
    }, 0);

    // Different task types have different batching efficiencies
    const efficiencies = {
      'VALUE_PROPS': 0.7, // 30% time savings - reuse research and themes
      'RESEARCH': 0.75, // 25% time savings - maintain research context
      'APPLICATION_SETUP': 0.8, // 20% time savings - similar processes
      'DOCUMENTS': 0.85, // 15% time savings - gather once, submit many
      'FINANCIAL': 0.85, // 15% time savings - reuse financial info
      'COLLEGE_DATA': 0.9, // 10% time savings - minor efficiencies
      'MISC': 0.95 // 5% time savings - minimal batching benefit
    };

    const efficiency = efficiencies[taskType] || 0.95;
    return baseTime * efficiency;
  }

  /**
   * Get appropriate title for batch based on type, application round, and tier
   */
  getBatchTitle(taskType, roundKey, tasks) {
    // Check if all tasks are from the same tier
    const tiers = [...new Set(tasks.map(t => this.getCollegeTier(t)))];
    const tierName = tiers.length === 1 ? tiers[0] : null;

    // Application round labels
    const roundLabels = {
      'early_decision': 'ED',
      'early_decision_1': 'ED',
      'early_decision_2': 'ED II',
      'early_action': 'EA',
      'regular': 'RD',
      'regular_decision': 'RD'
    };
    const roundLabel = roundLabels[roundKey] || '';

    // Base titles
    const baseTitle = {
      COLLEGE_DATA: `Update college information`,
      RESEARCH: `College research session`,
      VALUE_PROPS: `Value propositions workshop`,
      APPLICATION_SETUP: `Set up application portals`,
      DOCUMENTS: `Submit documents`,
      FINANCIAL: `Financial aid tasks`
    }[taskType] || `Complete ${tasks.length} similar tasks`;

    // Tier labels
    const tierLabels = {
      'reach': 'Reach schools',
      'target': 'Target schools',
      'safety': 'Safety schools',
      'unclassified': ''
    };

    // Build title with round and tier (for key task types)
    if (['VALUE_PROPS', 'RESEARCH', 'APPLICATION_SETUP'].includes(taskType)) {
      const parts = [baseTitle];

      // Add round if not RD (RD is default/most common)
      if (roundLabel && roundLabel !== 'RD') {
        parts.push(roundLabel);
      }

      // Add tier if single tier
      if (tierName && tierLabels[tierName]) {
        parts.push(tierLabels[tierName]);
      }

      return parts.join(' - ');
    }

    // For other task types, just add round if not RD
    if (roundLabel && roundLabel !== 'RD') {
      return `${baseTitle} - ${roundLabel}`;
    }

    return baseTitle;
  }

  /**
   * Get subtitle showing colleges involved
   */
  getBatchSubtitle(colleges, taskCount) {
    if (colleges.length === 0) {
      return `${taskCount} tasks`;
    }
    if (colleges.length <= 3) {
      return colleges.join(', ');
    }
    return `${colleges.slice(0, 2).join(', ')} and ${colleges.length - 2} more`;
  }

  /**
   * Calculate priority for a batch based on contained tasks
   */
  calculateBatchPriority(tasks) {
    const priorities = tasks.map(t => t.priority || 'normal');
    if (priorities.includes('critical')) return 'critical';
    if (priorities.includes('high')) return 'high';
    if (priorities.includes('medium')) return 'medium';
    return 'normal';
  }

  /**
   * Distribute batches across multiple days for better workload
   */
  distributeSessionsAcrossDays(batches, startDate, options = {}) {
    const {
      maxTasksPerDay = 4,
      preferWeekdays = true,
      sessionDays = 3
    } = options;

    const sessions = batches.filter(b => b.isSession);
    const regularBatches = batches.filter(b => !b.isSession);

    if (sessions.length === 0) {
      return batches;
    }

    // Distribute sessions across days
    const distributedSessions = [];
    let currentDay = 0;
    let currentDate = new Date(startDate);

    sessions.forEach((session, index) => {
      // Skip weekends if preferred
      if (preferWeekdays) {
        while (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
          currentDate = addDays(currentDate, 1);
        }
      }

      const distributedSession = {
        ...session,
        scheduled_date: format(currentDate, 'yyyy-MM-dd'),
        display_date: format(currentDate, 'yyyy-MM-dd'),
        sessionNumber: currentDay + 1,
        totalSessions: sessionDays
        // Don't add (Day X of Y) to title - it's shown separately
      };

      distributedSessions.push(distributedSession);

      // Move to next day after maxTasksPerDay
      if ((index + 1) % maxTasksPerDay === 0) {
        currentDay++;
        currentDate = addDays(currentDate, 1);
      }
    });

    return [...distributedSessions, ...regularBatches];
  }

  /**
   * Handle adding a new college to existing batches
   */
  addCollegeToBatches(existingBatches, newCollegeTasks) {
    const updatedBatches = [...existingBatches];

    newCollegeTasks.forEach(task => {
      if (!this.isTaskBatchable(task)) {
        // Add as individual task
        updatedBatches.push(task);
        return;
      }

      const taskType = this.getTaskBatchType(task);
      const taskDate = task.scheduled_date;

      // Find matching batch
      const matchingBatch = updatedBatches.find(batch =>
        batch.isBatch &&
        batch.batchType === taskType &&
        batch.scheduled_date === taskDate &&
        batch.tasks.length < 8 // Don't overcrowd batches
      );

      if (matchingBatch) {
        // Add to existing batch
        matchingBatch.tasks.push(task);
        matchingBatch.taskCount++;
        matchingBatch.estimated_hours += (task.estimated_hours || 0.5);
        if (!matchingBatch.colleges.includes(task.college_name)) {
          matchingBatch.colleges.push(task.college_name);
        }
        matchingBatch.subtitle = this.getBatchSubtitle(
          matchingBatch.colleges,
          matchingBatch.taskCount
        );
      } else {
        // Add as individual task (will be batched on next full refresh)
        updatedBatches.push(task);
      }
    });

    return updatedBatches;
  }

  /**
   * Check if a task is currently available to be completed
   * (e.g., AP registration has specific windows)
   */
  isTaskAvailable(task) {
    const title = (task.title || '').toLowerCase();
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentDay = currentDate.getDate();

    // AP Exam Registration - typically October-November
    if (title.includes('ap exam') || title.includes('ap registration')) {
      // AP registration typically opens in October and closes in November
      // This is a simplification - ideally would check with school calendar
      if (currentMonth < 9 || currentMonth > 10) { // Before October or after November
        return false;
      }
    }

    // SAT/ACT Registration - check if within registration window
    if (title.includes('sat registration') || title.includes('act registration')) {
      // Would need to check actual test dates and registration deadlines
      // For now, assume it's available if scheduled
      if (task.scheduled_date) {
        const scheduledDate = new Date(task.scheduled_date);
        const daysUntil = (scheduledDate - currentDate) / (1000 * 60 * 60 * 24);
        // Registration typically closes 4-5 weeks before test
        if (daysUntil < 30) {
          return false; // Too close to test date
        }
      }
    }

    // FAFSA - Opens October 1st
    if (title.includes('fafsa') || title.includes('financial aid')) {
      // FAFSA opens October 1st each year
      if (currentMonth < 9) { // Before October
        return false;
      }
    }

    // College portals - check if application is actually open
    if (title.includes('portal') || title.includes('application account')) {
      // Most applications open August 1st (Common App) or September 1st
      if (currentMonth < 7) { // Before August
        return false;
      }
    }

    // Default: assume task is available
    return true;
  }

  /**
   * Remove a college from existing batches
   */
  removeCollegeFromBatches(existingBatches, collegeId) {
    const updatedBatches = [];

    existingBatches.forEach(item => {
      if (!item.isBatch) {
        // Keep individual tasks not from this college
        if (item.college_id !== collegeId) {
          updatedBatches.push(item);
        }
      } else {
        // Filter out tasks from this college
        const remainingTasks = item.tasks.filter(t => t.college_id !== collegeId);

        if (remainingTasks.length >= 3) {
          // Keep as batch with updated info
          const updatedBatch = {
            ...item,
            tasks: remainingTasks,
            taskCount: remainingTasks.length,
            colleges: [...new Set(remainingTasks.map(t => t.college_name).filter(Boolean))],
            estimated_hours: remainingTasks.reduce((sum, t) => sum + (t.estimated_hours || 0.5), 0)
          };
          updatedBatch.subtitle = this.getBatchSubtitle(
            updatedBatch.colleges,
            updatedBatch.taskCount
          );
          updatedBatches.push(updatedBatch);
        } else {
          // Too few tasks, unbatch them
          updatedBatches.push(...remainingTasks);
        }
      }
    });

    return updatedBatches;
  }
}

export default new TaskBatchingService();